// Runtime context detection. We run the SPA in several shells:
//   - Browser (os.rx2go.ai): relative paths, same-origin backend.
//   - Electron packaged: loads `file://.../index.html` → protocol 'file:'.
//   - Electron dev (`npm run dev:desktop`): loads http://localhost:5173 (Vite),
//     identified by the preload-injected `window.electronAPI`.
//   - Capacitor mobile (bundled): loads the app from the WebView's local
//     scheme (`https://localhost` / `capacitor://localhost`) and injects
//     `window.Capacitor`. Like packaged Electron, the assets are local so the
//     backend must be an ABSOLUTE URL, not a relative path.

export const IS_CAPACITOR =
  typeof window !== 'undefined' && !!(window as any).Capacitor?.isNativePlatform

export const IS_ELECTRON = typeof window !== 'undefined' && (
  window.location.protocol === 'file:' ||
  !!window.electronAPI
)

// True whenever the SPA is served from local/bundled assets (not from the
// backend origin) and therefore must talk to an absolute backend URL.
export const IS_NATIVE_SHELL =
  IS_CAPACITOR || (IS_ELECTRON && window.location.protocol === 'file:')

// Backend host. Browser + Electron-dev (Vite proxy) → empty (relative paths).
// Packaged Electron + Capacitor mobile → absolute prod URL. Overridable at
// build time via VITE_BACKEND_URL so a staging/local bundled build can point
// elsewhere.
const BUILD_BACKEND = (typeof import.meta !== 'undefined'
  ? (import.meta as any).env?.VITE_BACKEND_URL
  : undefined) as string | undefined

export const BACKEND_URL = IS_NATIVE_SHELL
  ? (BUILD_BACKEND || 'https://os.rx2go.ai')
  : ''

// Resolve a possibly-relative media/upload path (avatar, photo, video,
// attachment) to a loadable URL. On web + Electron-dev BACKEND_URL is '' so
// relative paths stay same-origin; on packaged Electron / Capacitor it prepends
// the absolute backend origin. Already-absolute URLs (http/https/data/blob)
// and falsy values pass through untouched. Shared export consumed by the
// @os/messenger telegram components via the services/ sibling-rescue — keep the
// `mediaUrl` name + signature in sync with them.
export function mediaUrl(url?: string | null): string {
  if (!url) return ''
  if (/^(https?:|data:|blob:)/i.test(url)) return url
  return `${BACKEND_URL}${url}`
}
